import {
  Box, Button, Flashbar, Form, FormField,
  Header, Modal, Select, SpaceBetween, StatusIndicator, Table,
} from "@cloudscape-design/components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { API_BASE_URL } from "./config";
import { useDocumentTitle } from "./useDocumentTitle";

interface KiroPackage {
  name: string;
  credits: number;
  baseCost: number;
  feePct: number;
  totalCost: number;
}

interface KiroRequest {
  documentId: string;
  packageName: string;
  credits: number;
  totalCost: number;
  status: string;
  statusMessage: string | null;
  stakeholder: { firstName: string; lastName: string; email: string } | null;
}

interface OptionType {
  label: string;
  value: string;
}

// Statuses that are still settling and worth polling for updates.
const IN_FLIGHT = ["pending", "validating", "provisioning"];

const statusType = (status: string): "success" | "error" | "in-progress" | "stopped" | "info" => {
  if (status === "active" || status === "validated") return "success";
  if (status === "failed") return "error";
  if (status === "revoked") return "stopped";
  if (IN_FLIGHT.includes(status)) return "in-progress";
  return "info";
};

export default function KiroAccessPage() {
  useDocumentTitle("Kiro Access");
  const [requests, setRequests] = useState<KiroRequest[]>([]);
  const [packages, setPackages] = useState<KiroPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<KiroRequest[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [changeVisible, setChangeVisible] = useState(false);
  const [revokeVisible, setRevokeVisible] = useState(false);
  const [changePackage, setChangePackage] = useState<OptionType | null>(null);
  const [saving, setSaving] = useState(false);

  const apiKey = getAuthToken();
  const navigate = useNavigate();
  const selected = selectedItems[0];
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRequests = useCallback(() => {
    if (!apiKey) { setLoading(false); return; }
    apiFetch(`/kiro-subscription-requests?populate=stakeholder`)
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((json) => setRequests(json.data.map((item: any) => ({
        documentId: item.documentId,
        packageName: item.packageName,
        credits: item.credits,
        totalCost: item.totalCost,
        status: item.status,
        statusMessage: item.statusMessage ?? null,
        stakeholder: item.stakeholder
          ? { firstName: item.stakeholder.firstName, lastName: item.stakeholder.lastName, email: item.stakeholder.email }
          : null,
      }))))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  const fetchPackages = useCallback(() => {
    if (!apiKey) return;
    apiFetch(`/kiro-subscription-requests/packages`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load packages: ${res.status}`);
        return res.json();
      })
      .then((data: unknown) => {
        // The endpoint returns a bare array. Guard against error envelopes or
        // unexpected shapes so we never hand a non-array to .map().
        const list = Array.isArray(data)
          ? (data as KiroPackage[])
          : Array.isArray((data as any)?.data)
            ? ((data as any).data as KiroPackage[])
            : [];
        setPackages(list);
      })
      .catch((err) => setError(err.message));
  }, [apiKey]);

  useEffect(() => { fetchRequests(); fetchPackages(); }, [fetchRequests, fetchPackages]);

  // Poll the status of in-flight requests and patch them in place.
  useEffect(() => {
    const inFlight = requests.filter((r) => IN_FLIGHT.includes(r.status));
    if (inFlight.length === 0) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const current = requests.filter((r) => IN_FLIGHT.includes(r.status));
      await Promise.all(
        current.map(async (r) => {
          try {
            const res = await apiFetch(`/kiro-subscription-requests/${r.documentId}/status`);
            if (!res.ok) return;
            const data = await res.json();
            setRequests((prev) => prev.map((p) =>
              p.documentId === r.documentId
                ? { ...p, status: data.status, statusMessage: data.statusMessage ?? null }
                : p
            ));
          } catch { /* ignore transient poll errors */ }
        })
      );
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [requests]);

  const packageOptions: OptionType[] = packages.map((p) => ({
    label: `${p.name} — ${p.credits} credits — $${p.totalCost}/mo`,
    value: p.name,
  }));

  const orgCreds = async () => {
    const orgRes = await apiFetch(`/accounts/get-org-account`);
    if (!orgRes.ok) {
      const err = await orgRes.json();
      throw new Error(err.error?.message || `Could not resolve organization main account (${orgRes.status})`);
    }
    const orgAccount = await orgRes.json();
    const creds = getAwsCreds(orgAccount.alias);
    if (!creds) {
      throw new Error(
        `No AWS credentials found for the organization main account "${orgAccount.alias}". Add them on the Accounts page.`
      );
    }
    return creds;
  };

  const handleChange = async () => {
    if (!selected || !changePackage) return;
    setSaving(true);
    try {
      const creds = await orgCreds();
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/kiro-subscription-requests/${selected.documentId}/change-package`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-AWS-Access-Key-Id": creds.accessKeyId,
          "X-AWS-Secret-Access-Key": creds.secretAccessKey,
          "X-AWS-Session-Token": creds.sessionToken,
        },
        body: JSON.stringify({ packageName: changePackage.value }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: "Package changed" });
      setChangeVisible(false); setSelectedItems([]); fetchRequests();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const handleRevoke = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const creds = await orgCreds();
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/kiro-subscription-requests/${selected.documentId}/revoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-AWS-Access-Key-Id": creds.accessKeyId,
          "X-AWS-Secret-Access-Key": creds.secretAccessKey,
          "X-AWS-Session-Token": creds.sessionToken,
        },
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: "Subscription revoked" });
      setRevokeVisible(false); setSelectedItems([]); fetchRequests();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const openChange = () => {
    if (!selected) return;
    setChangePackage(packageOptions.find((o) => o.value === selected.packageName) ?? null);
    setChangeVisible(true);
  };

  const stakeholderName = (r: KiroRequest) =>
    r.stakeholder ? `${r.stakeholder.firstName} ${r.stakeholder.lastName} (${r.stakeholder.email})` : "-";

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Table
        variant="full-page" loading={loading} items={requests} selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as KiroRequest[])}
        trackBy="documentId"
        columnDefinitions={[
          { id: "stakeholder", header: "Stakeholder", cell: stakeholderName },
          { id: "packageName", header: "Package", cell: (item) => item.packageName },
          { id: "credits", header: "Credits", cell: (item) => item.credits },
          { id: "totalCost", header: "Total ($/mo)", cell: (item) => `$${item.totalCost}` },
          { id: "status", header: "Status", cell: (item) => (
            <StatusIndicator type={statusType(item.status)}>{item.status}</StatusIndicator>
          ) },
        ]}
        header={
          <Header counter={`(${requests.length})`} actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={!selected || selected.status === "revoked"} onClick={openChange}>Change package</Button>
              <Button disabled={!selected || selected.status === "revoked"} onClick={() => setRevokeVisible(true)}>Revoke</Button>
              <Button variant="primary" onClick={() => navigate("/kiro-access/request")}>Request Kiro access</Button>
            </SpaceBetween>
          }>Kiro Access</Header>
        }
        empty={
          <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
            {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No Kiro subscription requests found"}
          </Box>
        }
      />

      <Modal visible={changeVisible} onDismiss={() => setChangeVisible(false)} header="Change package"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setChangeVisible(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleChange} loading={saving}>Submit</Button>
        </SpaceBetween></Box>}>
        <Form>
          <FormField label="Package" description="Select the new Kiro package.">
            <Select
              selectedOption={changePackage}
              onChange={({ detail }) => setChangePackage(detail.selectedOption as OptionType)}
              options={packageOptions}
              placeholder="Select a package"
            />
          </FormField>
        </Form>
      </Modal>

      <Modal visible={revokeVisible} onDismiss={() => setRevokeVisible(false)} header="Revoke subscription"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setRevokeVisible(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleRevoke} loading={saving}>Revoke</Button>
        </SpaceBetween></Box>}>
        Are you sure you want to revoke the Kiro subscription for "{selected ? stakeholderName(selected) : ""}"?
      </Modal>
    </SpaceBetween>
  );
}
